/**
 * Product page: gallery, variant selection, add to cart, engraving field,
 * sticky add-to-cart bar, ring size guide dialog, recently viewed tracking.
 * Direction-aware: works the same in RTL (he) and LTR (en).
 */
(() => {
  const isRTL = () => document.documentElement.dir === 'rtl';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = () => (reducedMotion() ? 'auto' : 'smooth');

  if (!customElements.get('product-gallery')) {
    customElements.define(
      'product-gallery',
      class ProductGallery extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-track]');
          this.slides = [...this.querySelectorAll('.gallery__slide')];
          this.dots = [...this.querySelectorAll('[data-go]')];
          this.dialog = this.querySelector('.gallery__dialog');
          this.index = 0;

          // Track the active slide from scroll position. Works in RTL (negative scrollLeft) and LTR.
          if (this.slides.length > 1) {
            let ticking = false;
            this.track.addEventListener(
              'scroll',
              () => {
                if (ticking) return;
                ticking = true;
                requestAnimationFrame(() => {
                  const width = this.track.clientWidth || 1;
                  this.setActive(Math.round(Math.abs(this.track.scrollLeft) / width));
                  ticking = false;
                });
              },
              { passive: true }
            );
          }

          this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.go))));
          this.track.addEventListener('keydown', (event) => {
            const forward = isRTL() ? 'ArrowLeft' : 'ArrowRight';
            const back = isRTL() ? 'ArrowRight' : 'ArrowLeft';
            if (event.key === forward) this.goTo(this.index + 1);
            if (event.key === back) this.goTo(this.index - 1);
          });

          this.querySelectorAll('[data-zoom]').forEach((button) =>
            button.addEventListener('click', () => this.openZoom(Number(button.dataset.zoom)))
          );
          this.dialog?.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
          this.dialog?.addEventListener('click', (event) => {
            if (event.target === this.dialog) this.dialog.close();
          });

          document.addEventListener('variant:change', (event) => {
            const mediaId = event.detail.variant?.featured_media;
            if (!mediaId) return;
            const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
            if (slide) this.goTo(Number(slide.dataset.index));
          });
        }

        setActive(index) {
          this.index = index;
          this.dots.forEach((dot) => dot.toggleAttribute('aria-current', Number(dot.dataset.go) === index));
        }

        goTo(index) {
          const target = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
          if (!target) return;
          // Scroll the track only; avoid scrollIntoView so the page itself doesn't jump.
          const trackRect = this.track.getBoundingClientRect();
          const slideRect = target.getBoundingClientRect();
          const delta = isRTL() ? slideRect.right - trackRect.right : slideRect.left - trackRect.left;
          this.track.scrollBy({ left: delta, behavior: scrollBehavior() });
          this.setActive(Number(target.dataset.index));
        }

        openZoom(index) {
          if (!this.dialog) return;
          this.dialog.querySelectorAll('img[data-src]').forEach((img) => {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          });
          this.dialog.showModal();
          const image = this.dialog.querySelector(`[data-zoom-image="${index}"]`);
          image?.scrollIntoView({ block: 'start' });
          this.dialog.querySelector('[data-close]').focus();
        }
      }
    );
  }

  if (!customElements.get('product-page')) {
    customElements.define(
      'product-page',
      class ProductPage extends HTMLElement {
        connectedCallback() {
          this.data = JSON.parse(this.querySelector('[data-product-json]').textContent);
          this.form = this.querySelector('form.product-form');
          this.variantInput = this.form.querySelector('[data-variant-input]');
          this.addButton = this.form.querySelector('[data-add-button]');
          this.addLabel = this.form.querySelector('[data-add-label]');
          this.errorEl = this.querySelector('[data-form-error]');

          this.form.addEventListener('change', (event) => {
            if (event.target.matches('.option-picker__input')) this.onOptionChange();
          });
          this.form.addEventListener('submit', (event) => this.onSubmit(event));

          this.initEngraving();
          this.initStickyBar();
          this.initDialogs();
          this.trackRecentlyViewed();
        }

        selectedOptions() {
          return [...this.form.querySelectorAll('.option-picker')].map(
            (fieldset) => fieldset.querySelector('input:checked')?.value
          );
        }

        onOptionChange() {
          const options = this.selectedOptions();
          this.form.querySelectorAll('.option-picker').forEach((fieldset, i) => {
            const label = fieldset.querySelector('[data-option-value]');
            // Pills can show a clearer label than the stored value (earring quantity: "A pair").
            const pill = fieldset.querySelector('.option-picker__input:checked + .option-picker__pill');
            if (label) label.textContent = pill?.dataset.label || options[i];
          });

          const variant = this.data.variants.find((v) => v.options.every((value, i) => value === options[i]));
          this.updateAvailability(options);

          if (!variant) {
            this.setButton(false, this.data.strings.unavailable);
            return;
          }

          this.variantInput.value = variant.id;
          this.setButton(variant.available, variant.available ? this.data.strings.add : this.data.strings.sold_out);
          this.querySelectorAll('[data-price-container], [data-sticky-price]').forEach((el) => {
            el.innerHTML = variant.price_html;
          });
          const sku = this.querySelector('[data-sku]');
          if (sku && variant.sku) sku.textContent = variant.sku;

          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
          document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
        }

        // Mark option values that cannot form an available variant with the other current selections.
        updateAvailability(options) {
          this.form.querySelectorAll('.option-picker').forEach((fieldset, optionIndex) => {
            fieldset.querySelectorAll('.option-picker__input').forEach((input) => {
              const candidate = [...options];
              candidate[optionIndex] = input.value;
              const match = this.data.variants.find((v) => v.options.every((value, i) => value === candidate[i]));
              input.nextElementSibling?.classList.toggle('is-unavailable', !match || !match.available);
            });
          });
        }

        setButton(enabled, label) {
          this.addButton.disabled = !enabled;
          this.addLabel.textContent = label;
          const sticky = this.querySelector('[data-sticky-add]');
          if (sticky) {
            sticky.disabled = !enabled;
            sticky.textContent = label;
          }
        }

        async onSubmit(event) {
          event.preventDefault();
          if (this.addButton.disabled) return;
          if (!this.engravingValid()) return;
          this.errorEl.hidden = true;
          this.addButton.setAttribute('aria-busy', 'true');

          try {
            const body = new FormData(this.form);
            [...body.keys()].filter((key) => key.startsWith('option-')).forEach((key) => body.delete(key));
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
              method: 'POST',
              headers: { Accept: 'application/json', ...(this.igiChecked() ? { 'Content-Type': 'application/json' } : {}) },
              body: this.igiChecked() ? JSON.stringify(this.withIgi(body)) : body,
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.description || this.data.strings.error);
            const item = result.items ? result.items[0] : result;
            document.dispatchEvent(new CustomEvent('cart:add', { detail: { item, source: this.addButton } }));
            // Without a cart drawer listening, fall back to the cart page.
            if (!document.querySelector('cart-drawer')) {
              window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
            }
          } catch (error) {
            this.errorEl.textContent = error.message || this.data.strings.error;
            this.errorEl.hidden = false;
          } finally {
            this.addButton.removeAttribute('aria-busy');
          }
        }

        igiChecked() {
          return Boolean(this.form.querySelector('[data-igi-addon]')?.checked);
        }

        // The piece and its IGI certificate go to the cart in one request, tied by a shared hidden `_igi` property
        // so the cart can keep them together (quantity, removal).
        withIgi(formData) {
          const checkbox = this.form.querySelector('[data-igi-addon]');
          const token = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
          const properties = { _igi: token };
          for (const [key, value] of formData.entries()) {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match && value !== '') properties[match[1]] = value;
          }
          const quantity = Number(formData.get('quantity')) || 1;
          return {
            items: [
              { id: Number(formData.get('id')), quantity, properties },
              {
                id: Number(checkbox.dataset.variantId),
                quantity,
                properties: { [checkbox.dataset.forLabel]: this.data.strings.title, _igi: token },
              },
            ],
          };
        }

        initEngraving() {
          const wrapper = this.querySelector('[data-engraving]');
          if (!wrapper) return;
          const input = wrapper.querySelector('input');
          const count = wrapper.querySelector('[data-engraving-count]');
          const max = Number(wrapper.dataset.max) || 10;
          const lettersOnly = wrapper.classList.contains('engraving--letters');
          input.addEventListener('input', () => {
            if (lettersOnly) input.value = input.value.replace(/[^a-z]/gi, '').toUpperCase();
            const value = input.value.slice(0, max);
            count.textContent = value.length;
          });
          input.addEventListener('input', () => input.setCustomValidity(''));
        }

        // The form is novalidate (AJAX submit), so check the personalization field here with a translated message.
        engravingValid() {
          const wrapper = this.querySelector('[data-engraving]');
          const input = wrapper?.querySelector('input');
          if (!input) return true;
          input.setCustomValidity('');
          if (input.validity.valueMissing) input.setCustomValidity(wrapper.dataset.errorRequired);
          else if (input.validity.patternMismatch) input.setCustomValidity(wrapper.dataset.errorPattern);
          if (input.checkValidity()) return true;
          input.reportValidity();
          input.focus();
          return false;
        }

        initStickyBar() {
          const bar = this.querySelector('[data-sticky-atc]');
          if (!bar || !('IntersectionObserver' in window)) return;
          bar.hidden = false;
          const observer = new IntersectionObserver(([entry]) => {
            const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            bar.classList.toggle('is-visible', passed);
            bar.toggleAttribute('inert', !passed);
          });
          observer.observe(this.addButton);
          bar.toggleAttribute('inert', true);
          this.querySelector('[data-sticky-add]').addEventListener('click', () => {
            if (this.addButton.disabled) return;
            this.form.requestSubmit(this.addButton);
          });
        }

        initDialogs() {
          this.querySelectorAll('[data-open-dialog]').forEach((button) => {
            const dialog = this.querySelector(`#${button.dataset.openDialog}`);
            if (!dialog) return;
            button.addEventListener('click', () => {
              dialog.showModal();
              requestAnimationFrame(() => dialog.classList.add('is-visible'));
            });
            const close = () => {
              dialog.classList.remove('is-visible');
              setTimeout(() => {
                dialog.close();
                button.focus();
              }, reducedMotion() ? 0 : 350);
            };
            dialog.querySelector('[data-close]')?.addEventListener('click', close);
            dialog.addEventListener('click', (event) => {
              if (event.target === dialog) close();
            });
            dialog.addEventListener('cancel', (event) => {
              event.preventDefault();
              close();
            });
            dialog.querySelector('[data-print]')?.addEventListener('click', () => {
              document.documentElement.classList.add('is-printing-dialog');
              window.print();
              document.documentElement.classList.remove('is-printing-dialog');
            });
          });
        }

        trackRecentlyViewed() {
          if (this.hasAttribute('data-skip-recent')) return;
          try {
            const key = 'kc-recently-viewed';
            const handle = this.dataset.productHandle;
            const list = JSON.parse(localStorage.getItem(key) || '[]').filter((h) => h !== handle);
            list.unshift(handle);
            localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
          } catch (error) {
            // Storage unavailable: recently viewed simply stays empty.
          }
        }
      }
    );
  }
})();
